import { create } from "zustand";
import AppTicketConstant from "../assets/js/AppTicketConstant";
import { GetTicketById } from "../services/TicketService";
import {
  ApproverFilterTicketConfig,
  DealerFilterTicketConfig,
} from "../assets/js/FilterAppTicketConstant";
import { UserType } from "../assets/js/AppUserProfile";
import { ParamKey } from "../hooks/useQueryParams";

const useTicketStore = create((set, get) => ({
  ticketPhases: [],
  ticketStatus: {},
  allUniqueStatuses: [],
  phaseToGroupMap: {},
  phaseGroups: [],
  sortOptions: AppTicketConstant.sortOption,

  selectedPhase: null,
  selectedStatus: null,
  selectedSort: AppTicketConstant.sortOption[0],
  selectedMenu: null,
  isStatusDriven: false,

  otherFilter: {
    dealerName: [],
    dealerCode: [],
    carModel: [],
    activityType: [],
    activityPeriod: {
      start: null,
      end: null,
    },
  },

  formatOtherFilter: {
    dealer_ids: [],
    event_start_date: null,
    event_end_date: null,
    car_models: [],
    activities: [],
  },

  findTicketId: null,
  ticketDetail: {},

  pagination: {
    last_page: null,
    page_id: 1,
    page_size: 10,
    total_record: null,
  },

  activeFilters: {
    phases: [],
    statuses: [],
    sort_type: "DESC",
    sort_field: "CREATED_AT",
  },

  initFilterByRole: (userRole, useQueryParams) => {
    let filterConfig;

    switch (userRole) {
      case UserType.ADMIN:
      case UserType.REVER:
      case UserType.BUSINESS_CONTROL:
        filterConfig = ApproverFilterTicketConfig;
        break;
      case UserType.DEALER_BYD:
      case UserType.DEALER_DENZA:
      default:
        filterConfig = DealerFilterTicketConfig;
        break;
    }

    const phaseToGroupMap = {};
    filterConfig.phaseGroups.forEach((group) => {
      group.phases.forEach((phase) => {
        phaseToGroupMap[phase] = group.id;
      });
    });

    const filteredAllStatuses = filterConfig.allStatuses || [];
    const filteredTicketStatus = filterConfig.statuses || {};

    const allPhases = [];
    filterConfig.phaseGroups.forEach((group) => {
      if (group.phases) {
        allPhases.push(...group.phases);
      }
    });

    const uniqueStatusIds = [
      ...new Set(filteredAllStatuses.map((status) => status.id)),
    ];

    set({
      ticketPhases: filterConfig.phaseGroups,
      phaseGroups: filterConfig.phaseGroups,
      ticketStatus: filteredTicketStatus,
      phaseToGroupMap,
      allUniqueStatuses: filteredAllStatuses,
      activeFilters: {
        phases: allPhases,
        statuses: uniqueStatusIds,
        sort_type: "DESC",
        sort_field: "CREATED_AT",
      },
    });

    get().restoreFromUrl(useQueryParams);
  },

  updateUrlParams: (useQueryParams) => {
    try {
      const state = get();
      const {
        selectedPhase,
        selectedStatus,
        selectedSort,
        selectedMenu,
        otherFilter,
        pagination,
        findTicketId,
      } = state;

      const params = {};

      if (selectedMenu) {
        params.menu = selectedMenu;
      }

      if (selectedPhase) {
        params[ParamKey.Phases] = JSON.stringify(selectedPhase);
      }

      if (selectedStatus) {
        params[ParamKey.Statuses] = JSON.stringify(selectedStatus);
      }

      if (
        selectedSort &&
        selectedSort.id !== AppTicketConstant.sortOption[0].id
      ) {
        params[ParamKey.Sort] = JSON.stringify(selectedSort);
      }

      const hasOtherFilters =
        (otherFilter.dealerName && otherFilter.dealerName.length > 0) ||
        (otherFilter.dealerCode && otherFilter.dealerCode.length > 0) ||
        (otherFilter.carModel && otherFilter.carModel.length > 0) ||
        (otherFilter.activityType && otherFilter.activityType.length > 0) ||
        (otherFilter.activityPeriod &&
          (otherFilter.activityPeriod.start || otherFilter.activityPeriod.end));

      if (hasOtherFilters) {
        params[ParamKey.OtherFilter] = JSON.stringify(otherFilter);
      } else {
        params[ParamKey.OtherFilter] = null;
      }

      if (findTicketId) {
        params[ParamKey.TicketId] = findTicketId;
      }

      if (pagination && pagination.page_id !== 1) {
        params[ParamKey.Page] = JSON.stringify(pagination);
      }

      useQueryParams.setParams(params);
    } catch (error) {
      void error;
    }
  },

  updateActiveFilters: () => {
    try {
      const state = get();
      const { selectedPhase, selectedStatus, selectedSort, allUniqueStatuses } =
        state;

      let phases = [];
      let statuses = [];

      if (
        selectedPhase !== null &&
        selectedPhase !== undefined &&
        !selectedPhase.isSelectAll
      ) {
        if (selectedPhase.value && Array.isArray(selectedPhase.value)) {
          phases = selectedPhase.value.map((phase) => {
            if (typeof phase === "object" && phase.value) {
              return phase.value;
            }
            return phase;
          });
        } else if (
          selectedPhase.id &&
          typeof selectedPhase.id === "object" &&
          selectedPhase.id.value
        ) {
          phases = [selectedPhase.id.value];
        } else if (typeof selectedPhase.id === "string") {
          phases = [selectedPhase.id];
        }
      } else {
        if (selectedStatus) {
          const statusEntry = allUniqueStatuses.find(
            (s) => s.id === selectedStatus.id && s.name === selectedStatus.name
          );
          phases = statusEntry
            ? statusEntry.phase
            : ["PROPOSAL", "CLAIM", "MANAGEMENT", "ACCOUNTING"];
        } else {
          phases = ["PROPOSAL", "CLAIM", "MANAGEMENT", "ACCOUNTING"];
        }
      }

      if (selectedStatus) {
        statuses = [selectedStatus.id];
      } else {
        const relevantStatuses = allUniqueStatuses.filter((status) => {
          return status.phase.some((phase) => phases.includes(phase));
        });

        const uniqueStatusIds = [
          ...new Set(relevantStatuses.map((status) => status.id)),
        ];
        statuses = uniqueStatusIds;
      }

      let sort_type = "DESC";
      let sort_field = "CREATED_AT";

      if (selectedSort && selectedSort.id && selectedSort.id !== "DEFAULT") {
        const parts = selectedSort.id.split("_");
        if (parts.length > 1) {
          sort_type = parts.pop();
          sort_field = parts.join("_");
        }
      }

      const newActiveFilters = {
        phases: phases,
        statuses: statuses,
        sort_type,
        sort_field,
      };

      set({
        activeFilters: newActiveFilters,
      });
    } catch (error) {
      console.error(error);
    }
  },

  restoreFromUrl: (useQueryParams) => {
    try {
      const urlTicketId = useQueryParams.getParam(ParamKey.TicketId);
      const urlPhases = useQueryParams.getParam(ParamKey.Phases);
      const urlStatuses = useQueryParams.getParam(ParamKey.Statuses);
      const urlSort = useQueryParams.getParam(ParamKey.Sort);
      const urlOtherFilter = useQueryParams.getParam(ParamKey.OtherFilter);
      const urlPage = useQueryParams.getParam(ParamKey.Page);
      const urlMenu = useQueryParams.getParam("menu");

      let updates = {};

      if (urlTicketId) {
        updates.findTicketId = urlTicketId;
      }

      if (urlPhases) {
        try {
          updates.selectedPhase = JSON.parse(urlPhases);
        } catch (e) {
          void e;
        }
      }

      if (urlStatuses) {
        try {
          updates.selectedStatus = JSON.parse(urlStatuses);
        } catch (e) {
          void e;
        }
      }

      if (urlSort) {
        try {
          updates.selectedSort = JSON.parse(urlSort);
        } catch (e) {
          void e;
        }
      }

      if (urlMenu) {
        updates.selectedMenu = urlMenu;
      }

      if (urlOtherFilter) {
        try {
          const parsed = JSON.parse(urlOtherFilter);
          updates.otherFilter = {
            dealerName: parsed.dealerName || [],
            dealerCode: parsed.dealerCode || [],
            carModel: parsed.carModel || [],
            activityType: parsed.activityType || [],
            activityPeriod: {
              start: parsed.activityPeriod?.start || null,
              end: parsed.activityPeriod?.end || null,
            },
          };
        } catch (e) {
          void e;
        }
      }

      if (urlPage) {
        try {
          const parsed = JSON.parse(urlPage);
          updates.pagination = {
            ...get().pagination,
            last_page: parsed.last_page || null,
            page_id: parsed.page_id || 1,
            page_size: parsed.page_size || 10,
            total_record: parsed.total_record || null,
          };
        } catch (e) {
          void e;
        }
      }

      if (Object.keys(updates).length > 0) {
        updates.isStatusDriven =
          updates.selectedStatus !== null && updates.selectedPhase === null;
        set(updates);

        setTimeout(() => {
          get().updateActiveFilters();
          get().formatOtherFilterPayload();
        }, 0);
      }
    } catch (error) {
      void error;
    }
  },

  clearOtherFilterUrlParams: (useQueryParams) => {
    if (useQueryParams) {
      const currentParams = useQueryParams.getAllParams();

      delete currentParams[ParamKey.OtherFilter];

      useQueryParams.setParams(currentParams);
    }
  },

  resetFiltersKeepMenu: (useQueryParams = null) => {
    const currentMenu = get().selectedMenu;

    set((state) => ({
      selectedPhase: null,
      selectedStatus: null,
      selectedSort: AppTicketConstant.sortOption[0],
      isStatusDriven: false,
      selectedMenu: currentMenu,
      otherFilter: {
        dealerName: [],
        dealerCode: [],
        carModel: [],
        activityType: [],
        activityPeriod: {
          start: null,
          end: null,
        },
      },
      formatOtherFilter: {
        dealer_ids: [],
        event_start_date: null,
        event_end_date: null,
        car_models: [],
        activities: [],
      },
      findTicketId: null,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    get().updateActiveFilters();

    if (useQueryParams && currentMenu) {
      useQueryParams.setParams({ menu: currentMenu });
    } else if (useQueryParams) {
      useQueryParams.clearParams();
    }
  },

  setBulkOtherFilters: (newOtherFilter, useQueryParams = null) => {
    set((state) => ({
      otherFilter: { ...newOtherFilter },
      pagination: { ...state.pagination, page_id: 1 },
    }));

    get().formatOtherFilterPayload();

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setSelectedPhase: (phase, useQueryParams = null) => {
    set((state) => ({
      selectedPhase: phase,

      selectedStatus: phase && phase.isSelectAll ? null : state.selectedStatus,
      isStatusDriven: false,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    get().updateActiveFilters();

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setSelectedStatus: (status, useQueryParams = null) => {
    set((state) => ({
      selectedStatus: status,

      isStatusDriven: status !== null,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    get().updateActiveFilters();

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setSelectedSort: (sort, useQueryParams = null) => {
    set((state) => ({
      selectedSort: sort,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    get().updateActiveFilters();

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setOtherFilterValue: (filterKey, value, useQueryParams = null) => {
    set((state) => {
      if (!(filterKey in state.otherFilter)) return state;

      let newOtherFilter = { ...state.otherFilter };

      if (value && value.isSelectAll) {
        if (value.selectNone) {
          newOtherFilter[filterKey] = [];
        } else if (value.options && Array.isArray(value.options)) {
          const currentValues = Array.isArray(state.otherFilter[filterKey])
            ? [...state.otherFilter[filterKey]]
            : [];

          const existingIds = new Set(currentValues.map((item) => item.id));

          const newValues = [
            ...currentValues,
            ...value.options.filter((option) => !existingIds.has(option.id)),
          ];

          newOtherFilter[filterKey] = newValues;
        }
      } else if (filterKey === "activityPeriod") {
        newOtherFilter.activityPeriod = {
          start: value?.start || null,
          end: value?.end || null,
        };
      } else if (Array.isArray(state.otherFilter[filterKey])) {
        const currentValues = [...state.otherFilter[filterKey]];
        const valueIndex = currentValues.findIndex(
          (item) => item.id === value.id
        );

        if (valueIndex >= 0) {
          currentValues.splice(valueIndex, 1);
        } else {
          currentValues.push(value);
        }

        newOtherFilter[filterKey] = currentValues;
      }

      return {
        otherFilter: newOtherFilter,
        pagination: { ...state.pagination, page_id: 1 },
      };
    });

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setFindTicketId: (ticketId, useQueryParams = null) => {
    set((state) => ({
      findTicketId: ticketId,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  handlePageChange: (page_id, useQueryParams = null) => {
    set((state) => ({
      pagination: { ...state.pagination, page_id },
    }));

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  clearAllFilters: (useQueryParams = null) => {
    const currentPagination = get().pagination;
    const currentMenu = get().selectedMenu;

    set({
      selectedPhase: null,
      selectedStatus: null,
      selectedSort: AppTicketConstant.sortOption[0],
      selectedMenu: currentMenu,
      isStatusDriven: false,
      otherFilter: {
        dealerName: [],
        dealerCode: [],
        carModel: [],
        activityType: [],
        activityPeriod: {
          start: null,
          end: null,
        },
      },
      formatOtherFilter: {
        dealer_ids: [],
        event_start_date: null,
        event_end_date: null,
        car_models: [],
        activities: [],
      },
      findTicketId: null,
      pagination: {
        ...currentPagination,
        page_id: 1,
      },
    });

    get().updateActiveFilters();

    if (useQueryParams) {
      const currentMenuParam = useQueryParams.getParam("menu");
      useQueryParams.clearParams();

      if (currentMenuParam) {
        useQueryParams.setParams({ menu: currentMenuParam });
      }
    }
  },

  resetOtherFilters: (useQueryParams = null) => {
    const resetOtherFilter = {
      dealerName: [],
      dealerCode: [],
      carModel: [],
      activityType: [],
      activityPeriod: {
        start: null,
        end: null,
      },
    };

    const resetFormatOtherFilter = {
      dealer_ids: [],
      event_start_date: null,
      event_end_date: null,
      car_models: [],
      activities: [],
    };

    set((state) => ({
      otherFilter: resetOtherFilter,
      formatOtherFilter: resetFormatOtherFilter,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    if (useQueryParams) {
      setTimeout(() => {
        get().updateUrlParams(useQueryParams);
      }, 0);
    }
  },

  resetFilters: (useQueryParams = null) => {
    set((state) => ({
      selectedPhase: null,
      selectedStatus: null,
      selectedSort: AppTicketConstant.sortOption[0],
      isStatusDriven: false,
      pagination: { ...state.pagination, page_id: 1 },
      otherFilter: {
        dealerName: [],
        dealerCode: [],
        carModel: [],
        activityType: [],
        activityPeriod: {
          start: null,
          end: null,
        },
      },
      formatOtherFilter: {
        dealer_ids: [],
        event_start_date: null,
        event_end_date: null,
        car_models: [],
        activities: [],
      },
      findTicketId: null,
    }));

    get().updateActiveFilters();

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  formatOtherFilterPayload: () => {
    const state = get();

    const formatDateWithZeroTimeISO = (plainDateObject) => {
      if (
        !plainDateObject ||
        !plainDateObject.year ||
        !plainDateObject.month ||
        !plainDateObject.day
      )
        return null;

      try {
        const isoDateString = `${plainDateObject.year}-${String(
          plainDateObject.month
        ).padStart(2, "0")}-${String(plainDateObject.day).padStart(2, "0")}`;
        const jsDate = new Date(isoDateString);
        jsDate.setUTCHours(0, 0, 0, 0);
        return jsDate.toISOString();
      } catch {
        return null;
      }
    };

    const arrayMapperIds = (array) =>
      Array.isArray(array) ? array?.map((item) => item.id) : [];

    const newFormatOtherFilter = {
      dealer_ids: arrayMapperIds(state.otherFilter.dealerName),
      event_start_date: formatDateWithZeroTimeISO(
        state.otherFilter.activityPeriod.start
      ),
      event_end_date: formatDateWithZeroTimeISO(
        state.otherFilter.activityPeriod.end
      ),
      car_models: arrayMapperIds(state.otherFilter.carModel),
      activities: arrayMapperIds(state.otherFilter.activityType),
    };

    set({
      formatOtherFilter: newFormatOtherFilter,
    });

    return newFormatOtherFilter;
  },

  getAvailableStatusOptions: () => {
    const state = get();
    const { selectedPhase, ticketStatus, allUniqueStatuses } = state;

    if (!selectedPhase) {
      return allUniqueStatuses || [];
    }

    if (selectedPhase.isSelectAll) {
      return allUniqueStatuses || [];
    }

    let phaseKeys = [];

    if (selectedPhase.value && Array.isArray(selectedPhase.value)) {
      phaseKeys = selectedPhase.value.map((phase) => {
        if (typeof phase === "object" && phase.value) {
          return phase.value;
        }
        return phase;
      });
    } else if (
      selectedPhase.id &&
      typeof selectedPhase.id === "object" &&
      selectedPhase.id.value
    ) {
      phaseKeys = [selectedPhase.id.value];
    } else if (typeof selectedPhase.id === "string") {
      phaseKeys = [selectedPhase.id];
    }

    if (phaseKeys.length === 0) {
      return allUniqueStatuses || [];
    }

    const result = [];
    const seenStatusIds = new Set();

    phaseKeys.forEach((phaseKey) => {
      const statusesForPhase = ticketStatus[phaseKey];

      if (statusesForPhase && Array.isArray(statusesForPhase)) {
        statusesForPhase.forEach((status) => {
          if (!seenStatusIds.has(status.id)) {
            seenStatusIds.add(status.id);
            result.push(status);
          }
        });
      }
    });

    return result;
  },

  getFilterParams: () => {
    const state = get();
    const { activeFilters, pagination, formatOtherFilter, findTicketId } =
      state;

    const hasFormattedFilterValues =
      formatOtherFilter.dealer_ids.length > 0 ||
      formatOtherFilter.car_models.length > 0 ||
      formatOtherFilter.activities.length > 0 ||
      formatOtherFilter.event_start_date !== null ||
      formatOtherFilter.event_end_date !== null;

    return {
      phases: activeFilters.phases,
      statuses: activeFilters.statuses,
      sort_type: activeFilters.sort_type,
      sort_field: activeFilters.sort_field,
      ...(hasFormattedFilterValues ? formatOtherFilter : {}),
      ...(findTicketId ? { code: findTicketId } : {}),
      page_id: pagination.page_id,
      page_size: pagination.page_size,
    };
  },

  setSelectedMenu: (menuId, useQueryParams = null) => {
    set({ selectedMenu: menuId });
    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  clearSelectedPhase: (useQueryParams = null) => {
    set((state) => ({
      selectedPhase: null,
      selectedStatus: null,
      isStatusDriven: false,
      pagination: { ...state.pagination, page_id: 1 },
    }));

    get().updateActiveFilters();

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  removeOtherFilterValue: (
    filterKey,
    filterId = null,
    useQueryParams = null
  ) => {
    set((state) => {
      if (filterKey === "activityPeriod") {
        return {
          otherFilter: {
            ...state.otherFilter,
            activityPeriod: { start: null, end: null },
          },
        };
      }

      if (Array.isArray(state.otherFilter[filterKey])) {
        if (filterId === null) {
          return {
            otherFilter: {
              ...state.otherFilter,
              [filterKey]: [],
            },
          };
        }

        const updatedValues = state.otherFilter[filterKey].filter(
          (item) => item.id !== filterId
        );

        return {
          otherFilter: {
            ...state.otherFilter,
            [filterKey]: updatedValues,
          },
        };
      }

      return state;
    });

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setPagination: (newPagination, useQueryParams = null) => {
    set({ pagination: newPagination });

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  setPageId: (page_id, useQueryParams = null) => {
    set((state) => ({
      pagination: state.pagination ? { ...state.pagination, page_id } : null,
    }));

    if (useQueryParams) {
      get().updateUrlParams(useQueryParams);
    }
  },

  getSelectedFilters: () => {
    const state = get();
    return {
      phase: state.selectedPhase,
      status: state.selectedStatus,
      sort: state.selectedSort,
      menu: state.selectedMenu,
    };
  },

  hasActiveFilters: () => {
    const state = get();
    return !!(
      state.selectedPhase ||
      state.selectedStatus ||
      state.findTicketId ||
      state.otherFilter.dealerName.length > 0 ||
      state.otherFilter.dealerCode.length > 0 ||
      state.otherFilter.carModel.length > 0 ||
      state.otherFilter.activityType.length > 0 ||
      state.otherFilter.activityPeriod.start ||
      state.otherFilter.activityPeriod.end
    );
  },

  fetchTicketById: async (ticketId) => {
    const currentState = get();
    const existingTicket = currentState.ticketDetail[ticketId];

    if (existingTicket == null || Object.keys(existingTicket).length === 0) {
      const ticketData = await GetTicketById({ ticketId: ticketId });
      if (!ticketData) return;
      set((state) => ({
        ticketDetail: {
          ...state.ticketDetail,
          [ticketId]: ticketData.data,
        },
      }));
    }
  },

  getTicketDetailById: (ticketId) => {
    const state = get();
    return state.ticketDetail[ticketId] || null;
  },

  findAssignApprover: (ticketId, checkSequence) => {
    const state = get();
    const ticketDetail = state.getTicketDetailById(ticketId);

    if (!ticketDetail || !ticketDetail.approvers) return false;

    const checkAssignPhaseSequence = (approver, phase, sequenceNumbers) => {
      const phaseKey = `${phase.value.toLowerCase()}_phase_sequence_no`;
      return (
        approver[phaseKey] !== null &&
        sequenceNumbers.includes(approver[phaseKey])
      );
    };

    const hasAllRequiredApprovers = (requiredSequences) => {
      return Object.entries(requiredSequences).every(([phase, sequences]) => {
        return sequences.every((seq) =>
          ticketDetail.approvers.some((approver) =>
            checkAssignPhaseSequence(approver, { value: phase }, [seq])
          )
        );
      });
    };

    switch (checkSequence.type) {
      case "allPhase":
        return hasAllRequiredApprovers({
          proposal: [1, 2, 3],
          claim: [1, 2, 3],
          management: [1, 2],
          accounting: [1],
        });

      case "allPhase_Denza":
        return hasAllRequiredApprovers({
          proposal: [1, 2],
          claim: [1, 2],
          management: [1, 2],
          accounting: [1],
        });
      case "singlePhase":
        return ticketDetail.approvers.some((approver) =>
          checkAssignPhaseSequence(approver, checkSequence.phase, [
            checkSequence.sequenceNo,
          ])
        );

      case "customPhase":
        return Object.entries(checkSequence.phase).every(([phase, sequences]) =>
          sequences.every((seq) =>
            ticketDetail.approvers.some((approver) =>
              checkAssignPhaseSequence(approver, { value: phase }, [seq])
            )
          )
        );

      default:
        return false;
    }
  },
}));

export default useTicketStore;
